import { AiUser } from './AiUser'
import { BaseModel, UrlProviderLocation } from './common'
import { MachineLearningDataType, MachineLearningModel } from './MachineLearningModel'
import { VoiceModel, VoiceType } from './VoiceModel'

export interface OptimalEpochInfo {
  optimalEpoch: number
  lowestEpochSmoothLoss: number
}

export enum MLTrainingJobSpeed {
  QUICK = 'quick',
  NORMAL = 'normal'
}

export enum MLTrainingJobStatus {
  IDLE = 'idle',
  RUNNING = 'running',
  SUCCESS = 'success',
  ERROR = 'error',
  CANCELLED = 'cancelled'
}

export enum PitchMethod {
  RMVPE = 'rmvpe',
  MANGIO_CREPE = 'mangio-crepe',
  HYBRID = 'hybrid'
}

export const PITCH_HOP_LENGTH_OPTIONS = [20, 40, 60, 64, 80, 100, 120, 140, 160]

export enum RVCVersion {
  V1 = 'v1',
  V2 = 'v2'
}

export enum RVCSampleRate {
  _40K = '40k',
  _48K = '48k'
}

export enum TrainingJobTier {
  FREE = 'free',
  PREMIUM = 'premium'
}

/**
 * Make sure to update the MC_OR_HYBRID_TRAINED_D_WEIGHTS array if you add new pretrained D weights
 * that were trained using mangio-crepe or hybrid methods.
 */
export enum PretrainedDWeights {
  DEFAULT = 'undefined',
  UMV_250 = 'umv-d-250.pth',
  UMV2_200 = 'umv2-d-200.pth',
  UMV2_400 = 'umv2-d-400.pth',
  UMV2_600 = 'umv2-d-600.pth',
  UMV3_100 = 'umv3-d-100.pth',
  UMV3_300 = 'umv3-d-300.pth',
  UMV4_MC = 'umv4-d-150.pth',
  UMV5_400 = 'umv5-d-400.pth',
  UMV6_MULTI_100HR_250 = 'umv6_multi_100_d_epoch_250.pth',
  UMV6_MULTI_500HR_225 = 'umv6_multi_500_d_epoch_225.pth',
  UMV6_SPANISH_300 = 'umv6_spanish_d_epoch_300.pth',
  HYBRID_SLICERV3_70 = 'hybrid_slicerv3_d_70.pth'
}

export enum PretrainedGWeights {
  DEFAULT = 'undefined',
  UMV_250 = 'umv-g-250.pth',
  UMV2_200 = 'umv2-g-200.pth',
  UMV2_400 = 'umv2-g-400.pth',
  UMV2_600 = 'umv2-g-600.pth',
  UMV3_100 = 'umv3-g-100.pth',
  UMV3_300 = 'umv3-g-300.pth',
  UMV4_MC = 'umv4-g-150.pth',
  UMV5_400 = 'umv5-g-400.pth',
  UMV6_MULTI_100HR_250 = 'umv6_multi_100_g_epoch_250.pth',
  UMV6_MULTI_500HR_225 = 'umv6_multi_500_g_epoch_225.pth',
  UMV6_SPANISH_300 = 'umv6_spanish_g_epoch_300.pth',
  HYBRID_SLICERV3_70 = 'hybrid_slicerv3_g_70.pth'
}

// Represents pretrained weights that are compatible with hybrid pitch, due to being trained
// with either mangio-crepe or hybrid pitch methods, which use nonzero pitch values during silence.
export const MC_OR_HYBRID_TRAINED_D_WEIGHTS: Array<String> = [PretrainedDWeights.UMV4_MC]

/**
 * Returns the pitch method that should be used for both fine-tuning and inference based on the pretrained weights.
 * This ensures that pitch method lines up between pretrained weights, fine-tuning and inference.
 *
 * This is due to RMVPE sending 0s for pitch values during silence, while hybrid / crepe sends nonzero values.
 */
export function getPreferredPitchMethodFromPretrainedDWeights(
  pretrainedDWeights: string | undefined,
  hyrbidPitchEnabled: boolean
): PitchMethod {
  if (pretrainedDWeights) {
    if (MC_OR_HYBRID_TRAINED_D_WEIGHTS.includes(pretrainedDWeights)) {
      return hyrbidPitchEnabled ? PitchMethod.HYBRID : PitchMethod.MANGIO_CREPE
    }
  }
  return PitchMethod.RMVPE
}

export interface MLTrainingJobMetadata {
  maxEpochs: number
  saveCheckpointsEvery?: number
  preserveCheckpointFiles?: boolean
  pitchMethod?: PitchMethod
  pitchHopLength?: number
  crepeHopLength?: number // deprecated
  version: RVCVersion
  sampleRate?: RVCSampleRate
  pretrainedGWeightsUrl?: string // this is a filename (e.g. 'umv4-g-150.pth'), not a url
  pretrainedDWeightsUrl?: string // this is a filename (e.g. 'umv4-d-150.pth'), not a url
  experimentName: string
  name: string
  inputData: string[]
  image?: {
    name: string
    size: number
    extname: string
    mimeType: string
  }
  tags?: string[]
  youtubeLinks?: string[] // the youtube urls if this is a youtube dataset
  uvrInputPaths?: string[] // the paths to the files that are fed into the uvr
  uvrOutputPaths?: string[] // the paths to the files that are returned from uvr (usually with _vocals appended)
  needsUvr?: boolean
  deharmony?: boolean
  dereverb?: boolean
  splitInstrumental?: boolean
  normalize?: boolean
  voiceType?: VoiceType
  shouldAutoEqDataset?: boolean
  storageProvider?: UrlProviderLocation // The location where the model and index file are stored. Assume S3 if this is undefined.
}

export interface MLTrainingJob extends BaseModel {
  aiUserId: number
  aiUser: AiUser
  inputType: MachineLearningDataType
  outputType: MachineLearningDataType
  strapiMachineLearningModelId?: number
  machineLearningModelId?: number
  machineLearningModel?: MachineLearningModel
  jobStatus: MLTrainingJobStatus
  progress: number
  /**
   * This is only available on the `store` method response.
   */
  voiceModel?: VoiceModel
  metadata: MLTrainingJobMetadata
  tier: TrainingJobTier
  estimatedFinishedAt?: string // datestring
  logMessages?: string[]
  durationMs?: number
  trainSpeed: MLTrainingJobSpeed
}
